import { useState, useEffect, useRef, useCallback } from 'react';
import { useNotifications } from '@medprompt/shared';

// Upload history kept in session storage so a reload still flags a re-upload as a duplicate
const UPLOAD_HISTORY_KEY = 'medprompt_upload_history';
const HISTORY_DURATION_MS = 15 * 60 * 1000; // 15 minutes

const ALLOWED_EXTENSIONS = ['.pdf', '.jpeg', '.jpg', '.png', '.tif', '.tiff', '.doc', '.docx', '.rtf'];
const MAX_FILE_SIZE_BYTES = 4500000; // 4.5MB

const getUploadHistory = () => {
  try {
    const stored = sessionStorage.getItem(UPLOAD_HISTORY_KEY);
    if (!stored) return [];

    const history = JSON.parse(stored);
    const now = Date.now();

    // Filter out entries older than 15 minutes
    const validHistory = history.filter(entry =>
      (now - entry.timestamp) < HISTORY_DURATION_MS
    );

    // Save back filtered history
    if (validHistory.length !== history.length) {
      sessionStorage.setItem(UPLOAD_HISTORY_KEY, JSON.stringify(validHistory));
    }

    return validHistory;
  } catch (error) {
    console.error('Error reading upload history:', error);
    return [];
  }
};

const addToUploadHistory = (fileName, fileSize, status) => {
  try {
    const history = getUploadHistory();
    history.push({
      fileName,
      fileSize,
      timestamp: Date.now(),
      status
    });
    sessionStorage.setItem(UPLOAD_HISTORY_KEY, JSON.stringify(history));
  } catch (error) {
    console.error('Error adding to upload history:', error);
  }
};

const clearUploadHistory = () => {
  try {
    sessionStorage.removeItem(UPLOAD_HISTORY_KEY);
  } catch (error) {
    console.error('Error clearing upload history:', error);
  }
};

const checkForDuplicate = (fileName, fileSize) => {
  const history = getUploadHistory();
  return history.find(entry =>
    entry.fileName === fileName && entry.fileSize === fileSize
  );
};

const isPendingUpload = (item) => item.status === 'queued' || item.status === 'uploading' || item.status === 'duplicate';

/**
 * The upload queue (one upload at a time, with pseudo-progress), including deleting a split
 * document's original once every slice has uploaded. Window drag-and-drop stays on the documents
 * list so other pages do not become drop targets.
 */
const useDocumentUploads = ({ documentService, refreshDocuments }) => {
  const { addNotification } = useNotifications();
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadQueueModalOpen, setUploadQueueModalOpen] = useState(false);
  const progressTimersRef = useRef({});
  const processingRef = useRef(false);
  const uploadQueueRef = useRef([]);
  // Lets callers await a set of queued uploads reaching a terminal state (complete/error).
  // Used by the splitter's "delete original after upload" flow.
  const uploadBatchWaitersRef = useRef([]);

  // Load upload history from session storage into queue on mount
  useEffect(() => {
    const history = getUploadHistory();
    if (history.length === 0) return;

    const restoredItems = history.map(entry => ({
      id: `history-${entry.timestamp}-${Math.random()}`,
      file: null,
      fileName: entry.fileName,
      fileSize: entry.fileSize,
      status: entry.status === 'complete' ? 'complete' : 'error',
      progress: entry.status === 'complete' ? 100 : 0,
      startTime: null,
      completionTime: entry.timestamp,
      errorMessage: entry.status !== 'complete' ? 'Upload failed' : null,
      duplicateInfo: null
    }));

    uploadQueueRef.current = restoredItems;
    setUploadQueue([...uploadQueueRef.current]);
  }, []);

  // Validate and add file to upload queue
  const addFileToQueue = useCallback((file) => {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      addNotification(
        file.name,
        `Invalid file type. Only PDF, JPEG, PNG, TIFF, DOC, DOCX, and RTF files are allowed.`,
        { isError: true }
      );
      return false;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const fileSizeInMB = (file.size / (1000 * 1000)).toFixed(2);
      addNotification(
        file.name,
        `File size (${fileSizeInMB}MB) exceeds the maximum allowed size of ${4.5}MB.`,
        { isError: true }
      );
      return false;
    }

    const duplicate = checkForDuplicate(file.name, file.size);
    const status = duplicate ? 'duplicate' : 'queued';

    const uploadItem = {
      id: `${Date.now()}-${Math.random()}`,
      file: file,
      fileName: file.name,
      fileSize: file.size,
      status: status,
      progress: 0,
      startTime: null,
      completionTime: null, // Track when upload completes
      errorMessage: null,
      duplicateInfo: duplicate || null
    };

    uploadQueueRef.current = [...uploadQueueRef.current, uploadItem];
    setUploadQueue(uploadQueueRef.current);
    return uploadItem.id; // truthy id (or false on rejection) so callers can track the item
  }, [addNotification]);

  const notifyUploadItemTerminal = useCallback((uploadId, finalStatus) => {
    if (uploadBatchWaitersRef.current.length === 0) return;
    uploadBatchWaitersRef.current = uploadBatchWaitersRef.current.filter(waiter => {
      if (waiter.pending.has(uploadId)) {
        waiter.pending.delete(uploadId);
        waiter.results.set(uploadId, finalStatus);
      }
      if (waiter.pending.size === 0) {
        waiter.resolve(waiter.results);
        return false; // drop resolved waiter
      }
      return true;
    });
  }, []);

  const waitForUploadBatch = useCallback((uploadIds) => {
    return new Promise(resolve => {
      const ids = (uploadIds || []).filter(Boolean);
      if (ids.length === 0) {
        resolve(new Map());
        return;
      }
      uploadBatchWaitersRef.current.push({
        pending: new Set(ids),
        results: new Map(),
        resolve
      });
    });
  }, []);

  // Process a single upload item directly
  const processUploadById = useCallback((uploadItem) => {
    const uploadId = uploadItem.id;

    // Set the lock synchronously before any async work
    processingRef.current = uploadId;

    uploadQueueRef.current = uploadQueueRef.current.map(item =>
      item.id === uploadId
        ? { ...item, status: 'uploading', startTime: Date.now(), progress: 0 }
        : item
    );
    setUploadQueue([...uploadQueueRef.current]);

    // Start pseudo-progress timer
    const PROGRESS_DURATION = 30000;
    const PROGRESS_TARGET = 90;
    const INTERVAL = 100;
    const INCREMENT = (PROGRESS_TARGET / PROGRESS_DURATION) * INTERVAL;

    progressTimersRef.current[uploadId] = setInterval(() => {
      uploadQueueRef.current = uploadQueueRef.current.map(item => {
        if (item.id === uploadId && item.status === 'uploading' && item.progress < PROGRESS_TARGET) {
          return { ...item, progress: Math.min(item.progress + INCREMENT, PROGRESS_TARGET) };
        }
        return item;
      });
      setUploadQueue([...uploadQueueRef.current]);
    }, INTERVAL);

    documentService.uploadDocument(uploadItem.file)
      .then(() => {
        if (progressTimersRef.current[uploadId]) {
          clearInterval(progressTimersRef.current[uploadId]);
          delete progressTimersRef.current[uploadId];
        }

        addToUploadHistory(uploadItem.fileName, uploadItem.fileSize, 'complete');

        uploadQueueRef.current = uploadQueueRef.current.map(item =>
          item.id === uploadId
            ? { ...item, status: 'complete', progress: 100, completionTime: Date.now() }
            : item
        );
        setUploadQueue([...uploadQueueRef.current]);

        notifyUploadItemTerminal(uploadId, 'complete');

        // Release lock and start next
        processingRef.current = false;
        const nextQueued = uploadQueueRef.current.find(item => item.status === 'queued');
        if (nextQueued) {
          processUploadById(nextQueued);
        }
      })
      .catch((error) => {
        console.error('Error uploading file:', error);

        if (progressTimersRef.current[uploadId]) {
          clearInterval(progressTimersRef.current[uploadId]);
          delete progressTimersRef.current[uploadId];
        }

        addToUploadHistory(uploadItem.fileName, uploadItem.fileSize, 'error');

        uploadQueueRef.current = uploadQueueRef.current.map(item =>
          item.id === uploadId
            ? { ...item, status: 'error', errorMessage: error.message || 'Upload failed', completionTime: Date.now() }
            : item
        );
        setUploadQueue([...uploadQueueRef.current]);

        notifyUploadItemTerminal(uploadId, 'error');

        // Release lock and start next
        processingRef.current = false;
        const nextQueued = uploadQueueRef.current.find(item => item.status === 'queued');
        if (nextQueued) {
          processUploadById(nextQueued);
        }
      });
  }, [documentService, notifyUploadItemTerminal]);

  const startQueueProcessing = useCallback(() => {
    if (processingRef.current !== false) return;

    const firstQueued = uploadQueueRef.current.find(item => item.status === 'queued');
    if (firstQueued) {
      processUploadById(firstQueued);
    }
  }, [processUploadById]);

  // Confirm duplicate upload (change from 'duplicate' to 'queued' then kick off)
  const confirmDuplicateUpload = useCallback((uploadId) => {
    let confirmedItem = null;

    uploadQueueRef.current = uploadQueueRef.current.map(item => {
      if (item.id === uploadId && item.status === 'duplicate') {
        confirmedItem = { ...item, status: 'queued', duplicateInfo: null };
        return confirmedItem;
      }
      return item;
    });
    setUploadQueue([...uploadQueueRef.current]);

    if (processingRef.current === false && confirmedItem) {
      processUploadById(confirmedItem);
    }
  }, [processUploadById]);

  const enqueueFiles = useCallback((files) => {
    let addedCount = 0;
    files.forEach(file => {
      if (addFileToQueue(file)) {
        addedCount++;
      }
    });

    if (addedCount > 0) {
      setUploadQueueModalOpen(true);
      // Start processing after state update
      setTimeout(() => startQueueProcessing(), 100);
    }
  }, [addFileToQueue, startQueueProcessing]);

  // Handle file upload from input
  const handleFileUpload = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    enqueueFiles(files);

    // Reset file input so same file can be selected again
    event.target.value = '';
  }, [enqueueFiles]);

  // Receive the individual PDF slices produced by the splitter and enqueue them.
  // options.sourceDocumentId + options.deleteSourceAfter: when splitting an existing
  // document, delete the original — but only once every slice has uploaded successfully.
  const handleUploadSlices = useCallback((files, options = {}) => {
    const queuedIds = [];
    files.forEach(file => {
      const id = addFileToQueue(file);
      if (id) queuedIds.push(id);
    });

    if (queuedIds.length === 0) return;

    setUploadQueueModalOpen(true);
    // Start processing after state update
    setTimeout(() => startQueueProcessing(), 100);

    const { sourceDocumentId, deleteSourceAfter } = options;
    if (!sourceDocumentId || !deleteSourceAfter) return;

    // Only delete the source once every slice we just queued reaches a terminal state
    // and all of them succeeded. If any failed, keep the original (fail-safe).
    waitForUploadBatch(queuedIds).then(results => {
      const allSucceeded = queuedIds.every(id => results.get(id) === 'complete');
      if (!allSucceeded) {
        addNotification(
          `split-source-kept-${sourceDocumentId}`,
          'Some split documents failed to upload, so the original was kept. Please review the upload queue.',
          { isError: true }
        );
        return;
      }

      documentService.deleteDocument(sourceDocumentId)
        .then(() => {
          refreshDocuments();
          addNotification(
            `split-source-deleted-${sourceDocumentId}`,
            'Split complete. The original document was uploaded as separate documents and removed.'
          );
        })
        .catch(error => {
          console.error('Failed to delete source document after split:', error);
          addNotification(
            `split-source-delete-failed-${sourceDocumentId}`,
            'Split documents uploaded, but the original could not be deleted. Please delete it manually.',
            { isError: true }
          );
        });
    });
  }, [addFileToQueue, startQueueProcessing, waitForUploadBatch, addNotification, documentService, refreshDocuments]);

  // Cleanup progress timers on unmount
  useEffect(() => {
    const timers = progressTimersRef.current;
    return () => {
      Object.values(timers).forEach(timer => clearInterval(timer));
    };
  }, []);

  // Auto-cleanup old completed/error items from queue (matching 15-minute history window)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      uploadQueueRef.current = uploadQueueRef.current.filter(item => {
        if (['queued', 'uploading', 'duplicate'].includes(item.status)) return true;
        if (item.completionTime) return (now - item.completionTime) < HISTORY_DURATION_MS;
        return true;
      });
      setUploadQueue([...uploadQueueRef.current]);
    }, 60000);

    return () => clearInterval(cleanupInterval);
  }, []);

  const clearCompletedUploads = useCallback(() => {
    setUploadQueue(queue => queue.filter(item => item.status !== 'complete'));
  }, []);

  return {
    uploadQueue,
    pendingUploadCount: uploadQueue.filter(isPendingUpload).length,
    uploadQueueModalOpen,
    openUploadQueue: () => setUploadQueueModalOpen(true),
    closeUploadQueue: () => setUploadQueueModalOpen(false),
    enqueueFiles,
    handleFileUpload,
    confirmDuplicateUpload,
    clearUploadHistory,
    clearCompletedUploads,
    handleUploadSlices,
  };
};

export default useDocumentUploads;
